import { match, P } from 'ts-pattern';
import { produce } from 'immer';
import { BusEvent } from './EditorContext';
import { useMemo, useState } from 'react';
import { Subject, filter, map } from 'rxjs';
import { useSubscription } from '../hooks/useSubscription';
import { DraftData } from '../../api/Drafts';

export type DocumentEvent =
  | { type: 'INITIALISE'; document: DraftData }
  | { type: 'BODY_UPDATED'; body: string }
  | { type: 'TITLE_UPDATED'; title: string };

// Note(Zaymon): $$hack is toggled on every edit to propagate a state change
// even when the document state is unchanged. This is to stop the editor from
// being left in a dangling state when backspacing an empty document.
// We definitely shouldn't need this but for now... ¯\_(ツ)_/¯
// ...
// If we expose the document as a stream with shareReplay(1) we can probably get around this.
export type DocumentState = (DraftData & { $$hack: boolean }) | undefined;

const handleDocumentEvent = (event: DocumentEvent) => (state: DocumentState) =>
  produce(state, (draft) => {
    match(event)
      .with({ type: 'INITIALISE', document: P.select() }, (document) => {
        draft = { ...document, $$hack: true };
      })
      .with({ type: 'BODY_UPDATED', body: P.select() }, (body) => {
        if (draft) {
          draft.body = body;
          draft.$$hack = !draft.$$hack;
        } else {
          console.error("Can't update body of uninitialised document");
        }
      })
      .with({ type: 'TITLE_UPDATED', title: P.select() }, (title) => {
        if (draft) {
          draft.title = title;
          draft.$$hack = !draft.$$hack;
        } else {
          console.error("Can't update title of uninitialised document");
        }
      })
      .exhaustive();

    return draft;
  });

export const useDocumentState = (bus$: Subject<BusEvent>) => {
  const documentEvent$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Document'),
        map((e) => (e as { type: 'Document'; event: DocumentEvent }).event)
      ),
    [bus$]
  );

  const [documentState, setDocumentState] = useState<DocumentState>(undefined);

  useSubscription(
    () => documentEvent$.subscribe((e) => setDocumentState(handleDocumentEvent(e))),
    [documentEvent$, setDocumentState]
  );

  return documentState;
};
